import { useCallback, useMemo } from "react";
import { useAuth, useClerk, useUser } from "@clerk/react";
import posthog from "posthog-js";
import { router } from "@/router";
import { ga4SetUser } from "@/lib/analytics/ga4";

type LoginOptions = {
  appState?: { returnTo?: string };
  authorizationParams?: {
    organization?: string;
    redirect_uri?: string;
    screen_hint?: "signup" | string;
    connection?: string;
    login_hint?: string;
  };
};

type LogoutOptions = {
  logoutParams?: { returnTo?: string };
};

/**
 * Application-owned Clerk adapter.
 *
 * Keeping auth behind this hook lets feature code consume one stable contract while
 * the IdP remains an implementation detail. `LoginOptions` keeps its Auth0-shaped
 * fields so call sites didn't need to change when `loginWithRedirect` switched from
 * Clerk's hosted Account Portal to the in-app /sign-in and /sign-up routes.
 */
export const useAppAuth = () => {
  const { isLoaded, isSignedIn, getToken, orgId } = useAuth();
  const { user: clerkUser } = useUser();
  const clerk = useClerk();

  const user = useMemo(() => {
    if (!clerkUser) return undefined;
    const emailAddress = clerkUser.primaryEmailAddress;
    return {
      id: clerkUser.id,
      sub: clerkUser.id,
      org_id: orgId || undefined,
      email: emailAddress?.emailAddress,
      email_verified: emailAddress?.verification?.status === "verified",
      name: clerkUser.fullName || clerkUser.username || emailAddress?.emailAddress,
      given_name: clerkUser.firstName || undefined,
      family_name: clerkUser.lastName || undefined,
      picture: clerkUser.imageUrl,
    };
  }, [clerkUser, orgId]);

  const getAccessTokenSilently = useCallback(async () => {
    const token = await getToken();
    if (!token) throw new Error("No active Clerk session token");
    return token;
  }, [getToken]);

  const loginWithRedirect = useCallback(
    async (options: LoginOptions = {}) => {
      const returnTo = options.appState?.returnTo || "/dashboard";
      const callbackUrl =
        options.authorizationParams?.redirect_uri || `${window.location.origin}/auth-callback`;
      const organization = options.authorizationParams?.organization;
      const initialValues = options.authorizationParams?.login_hint
        ? { emailAddress: options.authorizationParams.login_hint }
        : undefined;

      if (organization && isSignedIn) {
        await clerk.setActive({ organization });
        window.location.assign(returnTo);
        return;
      }

      const isSignUp = options.authorizationParams?.screen_hint === "signup";
      // Imperative router navigation (not the `useNavigate` hook): this callback is
      // reachable from `AppWithAuth` in main.tsx, which renders above `<RouterProvider>`
      // and therefore has no Router context to hook into.
      router.navigate(isSignUp ? "/sign-up" : "/sign-in", {
        state: { callbackUrl, initialValues },
      });
    },
    [clerk, isSignedIn]
  );

  const logout = useCallback(
    async (options: LogoutOptions = {}) => {
      // The next person on this browser must not inherit this recruiter's analytics identity.
      try {
        if (posthog.__loaded) posthog.reset();
      } catch {
        // ignore
      }
      ga4SetUser(null);
      await clerk.signOut({ redirectUrl: options.logoutParams?.returnTo || "/" });
    },
    [clerk]
  );

  const setActiveOrganization = useCallback(
    async (organizationId: string) => {
      await clerk.setActive({ organization: organizationId });
    },
    [clerk]
  );

  return {
    isAuthenticated: Boolean(isSignedIn),
    isLoading: !isLoaded,
    user,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
    setActiveOrganization,
  };
};
